import {
  DropletOutlineIcon,
  GlucoseMonitorIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import { forwardRef } from "react"
import type {
  Ref,
  ComponentPropsWithoutRef,
} from "react"

import styles from "./FeedItemHeader.module.scss"
import { useEndedAtGlucoseTime } from "./useEndedAtGlucoseTime"
import { useFollowedUsers } from "src/contexts"
import { useReadableNearestGlucoseReading } from "src/hooks"
import type {
  ReadOnlyUserEvent,
  ReadOnlyUserEntry,
} from "src/types"

interface Props extends ComponentPropsWithoutRef<"div"> {
  userItem: ReadOnlyUserEvent | ReadOnlyUserEntry;
}

function EndedAtGlucoseValue(props: { endTime: string }) {
  const { currentFollow } = useFollowedUsers()
  const { endTime } = props

  const glucoseEnd = useReadableNearestGlucoseReading(
    currentFollow.following_account_id,
    endTime,
  )

  if (!glucoseEnd) {
    return null
  }

  const {
    readableValue,
    isUserEntry,
  } = glucoseEnd

  return (
    <>
      <pre className={styles.pre}>
        {" ➞ "}
      </pre>
      {isUserEntry
        ? <DropletOutlineIcon className={styles.summaryIcon} />
        : <GlucoseMonitorIcon className={styles.summaryIcon} />
      }
      <pre className={styles.pre}>
        {` ${readableValue}`}
      </pre>
    </>
  )
}

export const FeedItemHeaderGlucoseSummary = forwardRef<HTMLDivElement, Props>(
  function FeedItemHeaderGlucoseSummary(props: Props, propsRef: Ref<HTMLDivElement>) {
    const { userItem } = props

    const { currentFollow } = useFollowedUsers()
    const startTime = userItem.started_at
    const endTime = useEndedAtGlucoseTime(userItem)
    const glucoseStart = useReadableNearestGlucoseReading(
      currentFollow.following_account_id,
      startTime,
    )

    if (!glucoseStart) {
      return null
    }

    const {
      readableValue,
      isUserEntry,
    } = glucoseStart

    return (
      <div
        ref={propsRef}
        className={props.className}
      >
        {isUserEntry
          ? (
            <DropletOutlineIcon className={styles.summaryIcon}/>
          )
          : <GlucoseMonitorIcon className={styles.summaryIcon} />
        }
        <pre
          className={styles.pre}
          id={"glucose-value"}
        >
          {` ${readableValue}`}
        </pre>
        {endTime && (
          <EndedAtGlucoseValue endTime={endTime} />
        )}
      </div>
    )
  },
)
